import { useApiFetch, usePublicFetch } from '@/composables/useApiFetch'
import type { User, LoginCredentials, RegisterData, AuthResponse, BillingInfo } from '@/types'

export class AuthApiService {
  private $fetch = useApiFetch()
  private $publicFetch = usePublicFetch()

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.$publicFetch('login', {
      method: 'POST',
      body: credentials
    })
    
    return {
      user: this.transformUser(response.user),
      token: response.token,
      refreshToken: response.token // Laravel Sanctum uses same token
    }
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await this.$publicFetch('register', {
      method: 'POST',
      body: {
        name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        password_confirmation: userData.password
      }
    })
    
    return {
      user: this.transformUser(response.user),
      token: response.token,
      refreshToken: response.token
    }
  }

  async socialLogin(provider: string, token: string): Promise<AuthResponse> {
    const response = await this.$publicFetch('social-login', {
      method: 'POST',
      body: {
        provider,
        token
      }
    })
    
    return {
      user: this.transformUser(response.user),
      token: response.token,
      refreshToken: response.token
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.$fetch('me')
    return this.transformUser(response.user || response)
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await this.$fetch('users', {
      method: 'PUT',
      body: {
        name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone
      }
    })
    
    return this.transformUser(response.user || response)
  }

  async updateBillingInfo(billingInfo: BillingInfo): Promise<User> {
    const response = await this.$fetch('users', {
      method: 'PUT',
      body: {
        document_type: billingInfo.documentType,
        document_number: billingInfo.documentNumber,
        business_name: billingInfo.businessName
      }
    })
    
    return this.transformUser(response.user || response)
  }

  async uploadAvatar(file: File): Promise<string> {
    const formData = new FormData()
    formData.append('path_image', file)

    const response = await this.$fetch('users', {
      method: 'POST',
      body: formData,
      headers: {
        // Remove Content-Type to let browser set it with boundary for FormData
      }
    })
    
    return response.path_image || response.user?.path_image
  }

  async logout(): Promise<void> {
    await this.$fetch('logout', {
      method: 'POST'
    })
  }

  async forgotPassword(email: string): Promise<void> {
    await this.$publicFetch('forgot-password', {
      method: 'POST',
      body: { email }
    })
  }

  async resetPassword(token: string, email: string, password: string): Promise<void> {
    await this.$publicFetch('reset-password', {
      method: 'POST',
      body: {
        token,
        email,
        password,
        password_confirmation: password
      }
    })
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.$fetch('users/change-password', {
      method: 'POST',
      body: {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword
      }
    })
  }

  // Transform Laravel user model to frontend User type
  private transformUser(laravelUser: any): User {
    return {
      id: laravelUser.id?.toString() || '',
      email: laravelUser.email || '',
      firstName: laravelUser.name || '',
      lastName: laravelUser.last_name || '',
      role: 'customer', // Default role for ecommerce users
      phone: laravelUser.phone || '',
      avatar: laravelUser.path_image || '',
      billingInfo: laravelUser.document_type && laravelUser.document_number ? {
        documentType: laravelUser.document_type,
        documentNumber: laravelUser.document_number,
        businessName: laravelUser.business_name
      } : undefined,
      createdAt: new Date(laravelUser.created_at || Date.now()),
      updatedAt: new Date(laravelUser.updated_at || Date.now()),
    }
  }
}

export const authApiService = new AuthApiService()
